import { Heading } from "@ariakit/react";
import { MiddotSeparated } from "@indietabletop/appkit";
import { groupBy } from "es-toolkit";
import { Fragment } from "react";
import { Link } from "wouter";
import { superNormal } from "../../common/common.css.ts";
import type { Git } from "../../engine/Git.ts";
import type { Vehicle } from "../../engine/Vehicle.ts";
import { caps } from "../fmt.ts";
import { Divider } from "../forms/Divider.tsx";
import { modelLink } from "./style.css.ts";
import { useGangContext } from "./useGang.ts";

export type GangModelLinkProps = {
  name: string;
  type: string;
  loadout: string;
  cost: number | null;
  href: string;
  isRemoved?: boolean;
};

export function GangModelLink(props: GangModelLinkProps) {
  const { href, name, type, loadout, cost, isRemoved } = props;

  return (
    <Link href={href} className={modelLink.container({ isRemoved })}>
      <div className={modelLink.header}>
        <div className={modelLink.name}>{name}</div>
        {cost && <div className={modelLink.cost}>{caps(cost)}</div>}
      </div>

      <MiddotSeparated className={modelLink.loadout}>
        <span className={modelLink.type}>{type}</span>

        {!!loadout && <span>{loadout}</span>}
      </MiddotSeparated>
    </Link>
  );
}

function gitToProps(git: Git): GangModelLinkProps {
  return {
    name: git.name,
    type: git.type.name,
    loadout: git.loadoutSummary,
    cost: git.displayCost,
    href: `/g/${git.id}`,
    isRemoved: git.isDead,
  };
}

function vehicleToProps(vehicle: Vehicle): GangModelLinkProps {
  return {
    name: vehicle.name,
    type: vehicle.type.name,
    loadout: vehicle.loadoutSummary,
    cost: vehicle.displayCost,
    href: `/v/${vehicle.id}`,
    isRemoved: vehicle.isWriteOff,
  };
}

export function GangModelsList() {
  const { gang } = useGangContext();
  const { allGits, allVehicles } = gang;

  // Bad lib types. See https://github.com/toss/es-toolkit/issues/829
  // eslint-disable-next-line @typescript-eslint/no-useless-default-assignment
  const { active = [], removed = [] } = groupBy(
    [...allGits.map(gitToProps), ...allVehicles.map(vehicleToProps)],
    (model) => (model.isRemoved ? "removed" : "active"),
  );

  return (
    <div>
      <Divider />

      {active.map((model) => {
        return (
          <Fragment key={model.href}>
            <GangModelLink {...model} />
            <Divider />
          </Fragment>
        );
      })}

      {removed.length > 0 && (
        <>
          <Heading
            className={superNormal}
            style={{ fontSize: "1.25rem", marginBlock: "2.5rem 1rem" }}
          >
            In Memoriam
          </Heading>

          <Divider />

          {removed.map((model) => {
            return (
              <Fragment key={model.href}>
                <GangModelLink {...model} />
                <Divider />
              </Fragment>
            );
          })}
        </>
      )}
    </div>
  );
}
